import dayjs from 'dayjs';
import {
  AssociateInfo,
  GENDER_CODE,
  IMembershipLevelCost,
  INewMembershipInfo,
  IPayPlanCosts,
  Pages,
  DiscountRule,
  IPaperlessOptOut,
  GenderCode,
  MembershipLevel as enumMembershipLevel,
  BillPlan,
  CreateMembershipStatus,
} from '@lib/types';
import { NewJoinSessionData, NewMembershipFormData, ProductType } from '../application/models/NewJoinSessionData';
import {
  CartProductCode,
  CouponStatus,
  CurrentPayPlan,
  FlowType,
  MembershipLevel,
  RoleType,
} from '@ace-clublabs/common-membership-api-types';
import { autoPayBillPlans } from './constants';
import { getMembershipCosts } from '../containers/BenefitsPage';
import { Icon, Stack, Typography } from '@ace-clublabs/shared-component-library';

type Address = string | null | undefined;

export type GenderListItem = { genderName: string; genderValue: GenderCode };

export const getGenderList = (membershipInfo: INewMembershipInfo | null) => {
  const genderList: { genderName: string; genderValue: GenderCode }[] = [];
  for (const g of membershipInfo?.allowedGenders ?? []) {
    genderList.push({ genderName: GENDER_CODE[g], genderValue: g });
  }
  return genderList;
};

export const highlightAddressDifference = (address1: Address, address2: Address) => {
  if (!address2 || !address1) {
    return <></>;
  }

  return (
    <>
      {address2.split(' ').map((s: string, i: number) => {
        if (address1.split(' ')[i]?.toLowerCase() !== s?.toLowerCase()) {
          return (
            <span key={i} style={{ color: '#3E6B20' }}>
              {s}
              {i == address2.split(' ').length - 1 ? '' : ' '}
            </span>
          );
        } else {
          return <span key={i}>{s} </span>;
        }
      })}
    </>
  );
};

export const getLogInPage = (hostname: string): string => {
  if (hostname.search('localhost') > -1) {
    return Pages.ConfirmationPage;
  } else {
    if (hostname.startsWith('appdev1') || hostname.startsWith('appdev2')) {
      return 'https://appdev1.ace.aaa.com/enterprise/identity/dev/#/logout';
    } else if (hostname.startsWith('appqa1') || hostname.startsWith('appqa2')) {
      return 'https://appqa1.ace.aaa.com/enterprise/identity/qa/#/logout';
    } else if (hostname.startsWith('appuat1') || hostname.startsWith('appuat2')) {
      return 'https://appuat1.ace.aaa.com/enterprise/identity/uat/#/logout';
    } else {
      return 'https://app.ace.aaa.com/enterprise/identity/#/logout';
    }
  }
};

export type BenefitLevel = 'CLASSIC' | 'PLUS' | 'PREMIER';

export const eligibleForRV = (membershipInfo: INewMembershipInfo | null) => {
  if (!membershipInfo) {
    return false;
  }

  return membershipInfo.membershipProducts.RV?.length > 0;
};

export const eligibleForRVUpgrade = (membershipInfo: INewMembershipInfo | null, sessionData: NewJoinSessionData) => {
  if (!membershipInfo) {
    return false;
  }
  const { membershipProducts: products } = membershipInfo;
  const { membershipLevel: level } = sessionData.formData;
  const clubHasRV = eligibleForRV(membershipInfo);
  const rvMembershipLevels = products?.RV;

  return clubHasRV && !rvMembershipLevels?.includes(level as MembershipLevel);
};

export const eligibleForERS = (membershipInfo: INewMembershipInfo | null, sessionData: any) => {
  return membershipInfo?.membershipProducts?.ERS?.includes(sessionData.formData.membershipLevel);
};

export const getProductPrice = (
  membershipInfo: INewMembershipInfo | null,
  product: ProductType,
  sessionData: NewJoinSessionData
) => {
  if (!membershipInfo) {
    return '';
  }
  const { membershipCost } = membershipInfo;
  const { membershipLevel, billPlan } = sessionData.formData;
  const priceSuffix = billPlan == CurrentPayPlan.RECURRINGMONTHLY ? 'mo' : 'yr';

  if (!membershipLevel || !membershipCost) {
    return '';
  }

  switch (product) {
    case ProductType.ERS:
      return `$${membershipCost?.fees?.sameDayServiceFee}/once`;
    case ProductType.RV:
      const term = convertBillPlanToTerm(billPlan!);
      const membershipLevelCost = membershipCost[membershipLevel as BenefitLevel]!;
      const rvCost = membershipLevelCost[term]!.rv;

      if (!rvCost) {
        return '';
      }

      const rvNetCost = membershipLevelCost[term] ? rvCost.baseCost - rvCost.discountAmount : 0;
      return rvNetCost ? `$${rvNetCost}/${priceSuffix}` : 'Free';
    default:
      return '';
  }
};

export const getAssociatePrices = (membershipInfo: INewMembershipInfo, sessionData: NewJoinSessionData) => {
  if (!membershipInfo) {
    return '';
  }

  const { membershipCost } = membershipInfo;
  const { membershipLevel, associates: formDataAssociates, billPlan } = sessionData.formData;

  if (!membershipLevel || !membershipCost) {
    return '';
  }

  const associateCosts: number[] = [];
  const term = convertBillPlanToTerm(billPlan!);
  const membershipLevelCost = membershipCost[membershipLevel as BenefitLevel];
  const _costs = membershipLevelCost && membershipLevelCost[term];
  const costs = { ..._costs, membershipDiscounts: null };
  const priceSuffix = billPlan == CurrentPayPlan.RECURRINGMONTHLY ? 'mo' : 'yr';

  for (const key in costs) {
    const associateKeys = ['adult', 'associate', 'dependent'];
    if (associateKeys.includes(key)) {
      const cost = costs[key as keyof IPayPlanCosts];
      const baseCost = cost?.baseCost!;
      if (!associateCosts.includes(baseCost)) {
        associateCosts.push(baseCost);
      }
    }
  }

  //Check for Free Associate Discount
  const ml =
    membershipLevel == undefined ||
    membershipLevel == MembershipLevel.CLASSIC_RV ||
    membershipLevel == MembershipLevel.CLASSIC
      ? MembershipLevel.CLASSIC
      : membershipLevel == MembershipLevel.PLUS_RV || membershipLevel == MembershipLevel.PLUS
      ? MembershipLevel.PLUS
      : membershipLevel == MembershipLevel.PREMIER_RV || membershipLevel == MembershipLevel.PREMIER
      ? MembershipLevel.PREMIER
      : membershipLevel;
  const associates = formDataAssociates || [];
  const eligibleForFreeAssociate = membershipCost?.[ml]?.[term]?.membershipDiscounts?.find(
    ({ rule, count, benefitLevel }) => {
      return rule == DiscountRule.FREE_ASSOCIATE && count > associates.length && benefitLevel === membershipLevel;
    }
  );
  //Show Free if at Classic level
  if (eligibleForFreeAssociate) {
    return 'Free';
  }

  associateCosts.sort((a, b) => a - b);
  const listLength = associateCosts.length;
  if (listLength === 1) {
    return `$${associateCosts[0]}/${priceSuffix}`;
  } else {
    return `$${associateCosts[0]}-${associateCosts[listLength - 1]}/${priceSuffix}`;
  }
};

export const getAssociateBasePrice = (
  membershipInfo: INewMembershipInfo,
  roleType: RoleType,
  sessionData: NewJoinSessionData
) => {
  if (!membershipInfo) {
    return '';
  }
  const { membershipCost } = membershipInfo;
  const { membershipLevel, billPlan } = sessionData.formData;
  if (!membershipLevel || !membershipCost) {
    return '';
  }
  const term = convertBillPlanToTerm(billPlan!);
  const membershipLevelCost = membershipCost[membershipLevel as BenefitLevel];
  const costs = membershipLevelCost && membershipLevelCost[term];
  const priceSuffix = billPlan == CurrentPayPlan.RECURRINGMONTHLY ? 'mo' : 'yr';

  switch (roleType) {
    case RoleType.ADULT_ASSOCIATE:
      return `$${costs?.adult?.baseCost}/${priceSuffix}`;
    case RoleType.ASSOCIATE:
      return `$${costs?.associate?.baseCost}/${priceSuffix}`;
    case RoleType.DEPENDENT:
      return `$${costs?.dependent?.baseCost}/${priceSuffix}`;
    default:
      return '';
  }
};

export const getAssociateDiscountedPrice = (sessionData: NewJoinSessionData, associate: AssociateInfo) => {
  if (!sessionData) {
    return '';
  }
  const { billPlan } = sessionData.formData;
  const associatePriceCart = sessionData?.priceCart?.items.find((item) => item.id === associate.virtualId);
  const priceSuffix = billPlan == CurrentPayPlan.RECURRINGMONTHLY ? 'mo' : 'yr';

  if (!associatePriceCart) {
    return '';
  }

  const { discountedPrice, price } = associatePriceCart;
  if (typeof discountedPrice === 'number') {
    if (price - discountedPrice <= 0) {
      return 0;
    } else {
      const formattedPrice = discountedPrice ? `$${discountedPrice}/${priceSuffix}` : 'Free';
      return formattedPrice;
    }
  }
  return `$${price}/${priceSuffix}`;
};

export const getAssociateOriginalPrice = (sessionData: NewJoinSessionData, associate: AssociateInfo) => {
  if (!sessionData) {
    return '';
  }
  const { billPlan } = sessionData.formData;
  const associatePriceCart = sessionData?.priceCart?.items.find((item) => item.id === associate.virtualId);
  const priceSuffix = billPlan == CurrentPayPlan.RECURRINGMONTHLY ? 'mo' : 'yr';

  if (!associatePriceCart) {
    return '';
  }
  const { price } = associatePriceCart;
  return `$${price}/${priceSuffix}`;
};

export const convertBillPlanToTerm = (billPlan: CurrentPayPlan) => {
  return billPlan == CurrentPayPlan.RECURRINGMONTHLY ? 'monthly' : 'annual';
};

export const isAutopayRequired = (membershipInfo: INewMembershipInfo, formData: NewMembershipFormData): boolean => {
  return (
    !membershipInfo?.allowedBillPlans?.includes(CurrentPayPlan.ONETIME) ||
    formData?.billPlan === CurrentPayPlan.RECURRINGMONTHLY
  );
};

export const isAutopayAllowed = (membershipInfo: INewMembershipInfo): boolean => {
  return (
    membershipInfo?.allowedBillPlans?.includes(CurrentPayPlan.RECURRINGCREDITORDEBITCARD) ||
    membershipInfo?.allowedBillPlans?.includes(CurrentPayPlan.RECURRINGELECTRONICCHECK)
  );
};
export const isPaperlessOptOutAllowed = (paperlessOptOuts?: IPaperlessOptOut[]) => {
  return paperlessOptOuts?.some((po) => po.allowed == true);
};

export const isFullDigitalRequired = (paperlessOptOuts?: IPaperlessOptOut[]) => {
  return !isPaperlessOptOutAllowed(paperlessOptOuts) && paperlessOptOuts?.some((po) => po.selected == true);
};

export const isAutopayBillPlan = (formData: NewMembershipFormData): boolean => {
  return autoPayBillPlans.includes(formData.billPlan!);
};

export function getCartProductName<CartProductCode>(value: any): keyof CartProductCode {
  // @ts-ignore
  const keys = Object.keys(CartProductCode).filter((key) => CartProductCode[key as keyof CartProductCode] === value);
  return keys[0] as keyof CartProductCode;
}
export const showPaperless = (paperlessOptOuts?: IPaperlessOptOut[]) => {
  if (paperlessOptOuts && paperlessOptOuts.length > 0) return true;
  else return false;
};
export const calculateFamilyUpgradeCost = (
  membershipInfo: INewMembershipInfo,
  sessionData: NewJoinSessionData
): number => {
  const { membershipLevel, billPlan } = sessionData?.formData;
  const isMonthly = billPlan === CurrentPayPlan.RECURRINGMONTHLY;
  const memLevel: BenefitLevel = membershipLevel as BenefitLevel;
  if (!memLevel) return 0;

  const membershipCost: IMembershipLevelCost | undefined = membershipInfo?.membershipCost[memLevel];
  let baseCost;
  let discountAmount;
  if (isMonthly) {
    baseCost = membershipCost?.monthly?.familyUpgrade?.baseCost;
    discountAmount = membershipCost?.monthly?.familyUpgrade?.discountAmount;
  } else {
    baseCost = membershipCost?.annual?.familyUpgrade?.baseCost;
    discountAmount = membershipCost?.annual?.familyUpgrade?.discountAmount;
  }
  if (typeof baseCost !== 'number' || typeof discountAmount !== 'number') return 0;

  return baseCost - discountAmount;
};

export const stripNonWhitelistedQueryParams = (currentUrl: string, params: string) => {
  const whiteListedParams = params
    .split('&')
    .filter((param) => {
      const currentParam = param.split('=')[0];
      return ['zipcode', 'zip', 'campaigncode', 'couponcode'].includes(currentParam.toLowerCase());
    })
    .join('&');
  return currentUrl.split('?')[0] + `?${whiteListedParams}`;
};

export const getAutopayTextSection1 = (isMonthly: boolean) => {
  const yearlyText: string = 'Auto pay is required to join AAA.';
  const monthlyText: string = 'Auto pay is required to join AAA and is always required for the monthly dues option.';
  return isMonthly ? monthlyText : yearlyText;
};

export const getAutopayTextSection2 = (isMonthly: boolean) => {
  const yearlyText: string = '. You may cancel auto pay at any time from your My Account page.';
  const monthlyText: string =
    '. If you are purchasing a membership with the annual dues option you may cancel auto pay at any time from your My Account page.';
  return isMonthly ? monthlyText : yearlyText;
};

export const getFullDigitalDisclaimer = (clubCode: string) => {
  switch (clubCode) {
    case '215':
      return 'By selecting Pay now or Continue, you agree to opt in to paperless billing and to opt in to receiving a digital only membership card.';
      break;
    case '004':
      return 'By selecting Pay now or Continue, you agree to opt in to paperless billing and to opt in to receiving a digital only membership card.';
      break;
  }
  return 'By selecting Pay now or Continue, you agree to opt in to paperless billing and to opt in to receiving a digital only membership card and digital only AAA Explorer magazine.';
};

export const getMessageList = (membershipInfo: INewMembershipInfo | null): any[] => {
  if (!membershipInfo) {
    return [];
  }
  const { giftDetails } = membershipInfo;
  const seasons = giftDetails?.seasons;
  const messages = giftDetails?.messages;
  if (!seasons) {
    return [];
  }
  const today = new Date();

  const eligibleSeasons = seasons.filter(({ startDate, endDate }) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    return today >= start && today <= end;
  });

  const validHolidayTypes = new Set(eligibleSeasons.map(({ type }) => type));

  let messageList;
  messageList = messages!
    .filter(({ type }) => type === '' || validHolidayTypes.has(type))
    .sort((a, b) => (a.type === '' ? 1 : -1));

  return messageList ?? [];
};

export const getMembershipTitle = (membershipLevel?: MembershipLevel, hasRV?: boolean) => {
  switch (membershipLevel) {
    case MembershipLevel.CLASSIC:
      return hasRV ? 'Classic RV' : 'Classic';
    case MembershipLevel.PLUS:
      return hasRV ? (
        <>
          Plus<sup style={{ fontSize: '0.6em', verticalAlign: 'text-bottom' }}>®</sup> RV
        </>
      ) : (
        <>
          Plus<sup style={{ fontSize: '0.6em', verticalAlign: 'text-bottom' }}>®</sup>
        </>
      );

    case MembershipLevel.PREMIER:
      return hasRV ? (
        <>
          Premier<sup style={{ fontSize: '0.6em', verticalAlign: 'text-bottom' }}>®</sup> RV
        </>
      ) : (
        <>
          Premier<sup style={{ fontSize: '0.6em', verticalAlign: 'text-bottom' }}>®</sup>
        </>
      );

    case MembershipLevel.CLASSIC_RV:
      return 'Classic RV';
    case MembershipLevel.PLUS_RV:
      return (
        <>
          Plus<sup style={{ fontSize: '0.6em', verticalAlign: 'text-bottom' }}>®</sup> RV
        </>
      );
    case MembershipLevel.PREMIER_RV:
      return (
        <>
          Premier<sup style={{ fontSize: '0.6em', verticalAlign: 'text-bottom' }}>®</sup> RV
        </>
      );
    default:
      return '';
  }
};

export const startDateFromNowInDays = (startDate: string | Date): number => {
  const today = new Date();
  const targetDate = new Date(startDate);
  const diffInMs = targetDate.getTime() - today.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
  return diffInDays;
};

export const getDate7DaysLaterFormatted = (): string => {
  return dayjs().add(7, 'day').format('MM/DD/YYYY');
};

export const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};
export type MembershipTerm = 'annual' | 'monthly';

const applyAutopayDiscount = (
  membershipCosts: IPayPlanCosts,
  membershipInfo: INewMembershipInfo,
  sessionData: NewJoinSessionData
): boolean => {
  const primaryMemberBaseCost = membershipCosts.primary?.baseCost;
  const primaryMemberDiscountAmount = membershipCosts.primary?.discountAmount;
  const autoPayDiscount = membershipCosts.autoPay?.discountAmount ?? 0;
  const primaryMemberCostTotal = primaryMemberBaseCost - primaryMemberDiscountAmount;
  const { billPlan, previousAnnualBillPlan } = sessionData.formData;
  const AC = CurrentPayPlan.RECURRINGCREDITORDEBITCARD;
  const isAnnualBillPlanAC = billPlan === AC || previousAnnualBillPlan === AC;
  const AMNotAllowed = !membershipInfo?.allowedBillPlans.includes(BillPlan.AM);
  return (isAnnualBillPlanAC || AMNotAllowed) && primaryMemberCostTotal > autoPayDiscount;
};

const calculateDisplayCost = (
  sessionData: NewJoinSessionData,
  membershipCosts: IPayPlanCosts,
  membershipInfo: INewMembershipInfo
): number => {
  const primaryMemberBaseCost = membershipCosts.primary?.baseCost;

  // This was used by the original design of Major rocket
  // We were returning baseCost to cross out, instead of displaying free as price
  // might bring this design back in the future
  // const isMajorRocket = sessionData.flowType === FlowType.MAJOR_ROCKET;
  // if (isMajorRocket) {
  //   return primaryMemberBaseCost;
  // }

  // Calculate the discounts
  const primaryMemberDiscountAmount = membershipCosts.primary?.discountAmount;
  const autoPayDiscount = membershipCosts.autoPay?.discountAmount ?? 0;
  const primaryMemberCostTotal = primaryMemberBaseCost - primaryMemberDiscountAmount;

  // Otherwise calculate the discounted price and only display if autopay is required
  const showDiscountedPrice = applyAutopayDiscount(membershipCosts, membershipInfo, sessionData);
  if (showDiscountedPrice) {
    return primaryMemberCostTotal - autoPayDiscount;
  }
  return primaryMemberCostTotal;
};

export const getCostByMembershipLevel = (
  membershipInfo: INewMembershipInfo,
  memLevel: MembershipLevel,
  selectedTerm: MembershipTerm,
  sessionData: NewJoinSessionData
) => {
  const membershipCost = getMembershipCosts(membershipInfo!, memLevel, selectedTerm);

  return calculateDisplayCost(sessionData, membershipCost!, membershipInfo);
};

export const getBaseCostByMembershipLevel = (
  memLevel: MembershipLevel,
  membershipInfo: INewMembershipInfo,
  selectedTerm: MembershipTerm
) => {
  let baseCostObject = null;

  switch (memLevel) {
    case MembershipLevel.CLASSIC:
      baseCostObject = membershipInfo?.membershipCost[MembershipLevel.CLASSIC]?.[selectedTerm];
      break;
    case MembershipLevel.PLUS:
      baseCostObject = membershipInfo?.membershipCost[MembershipLevel.PLUS]?.[selectedTerm];
      break;
    case MembershipLevel.PREMIER:
      baseCostObject = membershipInfo?.membershipCost[MembershipLevel.PREMIER]?.[selectedTerm];
      break;
  }
  return (baseCostObject as IPayPlanCosts)?.primary?.baseCost;
};

export const iconMap = (membershipLevel: MembershipLevel) => {
  switch (membershipLevel) {
    case MembershipLevel.CLASSIC:
    case MembershipLevel.CLASSIC_RV:
      return (
        <Icon
          iconName="heart-solid"
          sx={{ color: '#B0c4DE', fontSize: '1.25rem', stroke: 'rgb(1, 107, 205)', strokeWidth: '2' }}
        />
      );
    case MembershipLevel.PLUS:
    case MembershipLevel.PLUS_RV:
      return <Icon iconName="award-duotone" sx={{ color: '#BE3317', fontSize: '1.25rem' }} />;
    case MembershipLevel.PREMIER:
    case MembershipLevel.PREMIER_RV:
      return <Icon iconName="gem-duotone" sx={{ color: '#7527B9', fontSize: '1.25rem' }} />;
    default:
      return '';
  }
};

export const historyPushState = (state: any, title: string, url?: string | null) => {
  window.history.pushState(state, title, url);

  const event = new CustomEvent('custom:pushstate', {
    detail: { state, title, url },
  });

  window.dispatchEvent(event);
};

export const getDefaultMembershipLevel = (allowedMembershipLevels: MembershipLevel[] | undefined) => {
  if (!allowedMembershipLevels) {
    return MembershipLevel.CLASSIC;
  }

  if (allowedMembershipLevels.length === 1) {
    return allowedMembershipLevels[0];
  }

  const levelHierarchy = [MembershipLevel.CLASSIC, MembershipLevel.PLUS, MembershipLevel.PREMIER];

  const orderedLevelItems = allowedMembershipLevels?.sort((a, b) => {
    return levelHierarchy.indexOf(a) - levelHierarchy.indexOf(b);
  });

  return orderedLevelItems[0];
};

export const mapFromCreateMembershipStatusToCouponStatus = (
  createMembershipStatus: CreateMembershipStatus
): CouponStatus => {
  if (createMembershipStatus == CreateMembershipStatus.GROUPON_COUPON_EXPIRED) {
    return CouponStatus.Expired;
  } else if (createMembershipStatus == CreateMembershipStatus.GROUPON_COUPON_REDEEMED) {
    return CouponStatus.Redeemed;
  } else if (createMembershipStatus == CreateMembershipStatus.GROUPON_COUPON_REFUNDED) {
    return CouponStatus.Refunded;
  } else {
    return CouponStatus.Active;
  }
};

export const getProgressProps = (
  flow: FlowType,
  page: Pages
): { description: string; currentStep: number; totalSteps: number; percentage: number } => {
  let result: any;
  if (flow === FlowType.GIFT) {
    switch (page) {
      case Pages.DateSelector:
        result = { description: 'select start date', currentStep: 1 };
        break;
      case Pages.ContactDetailsPage:
        result = { description: 'gift recipient information', currentStep: 2 };
        break;
      case Pages.AddressPage:
        result = { description: 'gift recipient address', currentStep: 3 };
        break;
      case Pages.GiftDonorContactPage:
        result = { description: 'gift giver information', currentStep: 4 };
        break;
      case Pages.GiftDonorAddressPage:
        result = { description: 'gift giver address', currentStep: 5 };
        break;
      case Pages.SendMembershipCardPage:
        result = { description: 'gift delivery', currentStep: 6 };
        break;
      case Pages.SendGiftCardPage:
        result = { description: 'gift card', currentStep: 7 };
        break;
      case Pages.BuildMembershipPage:
        result = { description: 'customize membership', currentStep: 8 };
        break;
      case Pages.ReviewPage:
        result = { description: 'review', currentStep: 9 };
        break;
      default:
        result = { description: '', currentStep: 0 };
    }
    result.totalSteps = 9;
  } else {
    switch (page) {
      case Pages.ContactDetailsPage:
        result = { description: 'member information', currentStep: 1 };
        break;
      case Pages.AddressPage:
        result = { description: 'address', currentStep: 2 };
        break;
      case Pages.BuildMembershipPage:
        result = { description: 'customize membership', currentStep: 3 };
        break;
      case Pages.ReviewPage:
        result = { description: 'review', currentStep: 4 };
        break;
      case Pages.AutopayPage:
        result = { description: 'Auto Pay enrollment', currentStep: 5 };
        break;
      default:
        result = { description: '', currentStep: 0 };
    }
    result.totalSteps = 5;
  }
  result.percentage = (result.currentStep / result.totalSteps) * 100;
  return result;
};
